import { useEffect, useState } from 'react';
import type { HostingProviderSummary, HostingProviderPage } from '@tosho/contracts';
import { BusinessLogo } from './BusinessLogo';
import {
  hostingProviderApi,
  hostingProviderError,
  hostingProviderLogoUrl,
} from './hosting-provider-api';

/** Sélection explicite et paginée ; le texte libre historique reste disponible à côté. */
export function HostingProviderPicker({
  value,
  onChange,
}: {
  readonly value: HostingProviderSummary | null;
  readonly onChange: (value: HostingProviderSummary | null) => void;
}): React.JSX.Element {
  const [q, setQ] = useState(''),
    [page, setPage] = useState(0),
    [visit, setVisit] = useState(0);
  const [data, setData] = useState<HostingProviderPage | null>(null),
    [loading, setLoading] = useState(true),
    [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    const timer = window.setTimeout(() => {
      void hostingProviderApi
        .list(q, page)
        .then((result) => {
          if (active) setData(result);
        })
        .catch((reason) => {
          if (active) {
            setData(null);
            setError(hostingProviderError(reason));
          }
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }, 200);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [q, page, visit]);
  return (
    <div className="hosting-provider-picker">
      {value && (
        <div className="hosting-provider-picker__selected">
          <BusinessLogo name={value.displayName} logoUrl={hostingProviderLogoUrl(value)} />
          <span className="hosting-provider-picker__selection-name">Hébergeur choisi</span>
          <button type="button" onClick={() => onChange(null)}>
            Détacher l’hébergeur
          </button>
        </div>
      )}
      <label>
        Rechercher un hébergeur du catalogue
        <input
          value={q}
          maxLength={120}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(0);
          }}
        />
      </label>
      {loading ? (
        <p role="status">Recherche des hébergeurs…</p>
      ) : error ? (
        <p className="tosho-feedback is-error" role="alert">
          {error}{' '}
          <button type="button" onClick={() => setVisit((n) => n + 1)}>
            Réessayer
          </button>
        </p>
      ) : (
        <>
          <ul aria-label="Hébergeurs disponibles">
            {data?.items.map((provider) => (
              <li key={provider.id}>
                <button
                  type="button"
                  aria-label={'Choisir ' + provider.displayName}
                  aria-pressed={value?.id === provider.id}
                  onClick={() => onChange(provider)}
                >
                  <BusinessLogo
                    name={provider.displayName}
                    logoUrl={hostingProviderLogoUrl(provider)}
                  />
                </button>
              </li>
            ))}
          </ul>
          {data?.items.length === 0 && (
            <p>Aucun hébergeur dans ces résultats. Le catalogue Hébergeurs permet d’en ajouter.</p>
          )}
          <div className="hosting-provider-picker__pages">
            <button type="button" disabled={page === 0} onClick={() => setPage((n) => n - 1)}>
              Hébergeurs précédents
            </button>
            <button
              type="button"
              disabled={!data || (page + 1) * data.pageSize >= data.total}
              onClick={() => setPage((n) => n + 1)}
            >
              Hébergeurs suivants
            </button>
          </div>
        </>
      )}
    </div>
  );
}
